/**
 * Progressive enhancement: restore product/collection quote context when the
 * quote page is opened with ?product=handle or ?collection=handle.
 */
(() => {
  const root = document.querySelector('[data-nadm-quote-context="manual"]');
  if (!root) return;

  const params = new URLSearchParams(window.location.search);
  const productHandle = (params.get('product') || '').trim();
  const collectionHandle = (params.get('collection') || '').trim();
  if (!productHandle && !collectionHandle) return;

  const form = root.querySelector('form');
  const message = root.querySelector('textarea[name="contact[body]"]');
  const layout = root.querySelector('.nadm-quote-form__layout');
  if (!form || !layout) return;

  const escapeHtml = (value) =>
    String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');

  const ensureHidden = (name, value) => {
    let input = form.querySelector(`input[name="${name}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  };

  const renderContext = ({ mode, title, url, meta, imageUrl, note }) => {
    root.dataset.nadmQuoteContext = mode;
    root.classList.remove('nadm-quote-form--context-manual');
    root.classList.add(`nadm-quote-form--context-${mode}`);
    layout.classList.add('nadm-quote-form__layout--split');

    ensureHidden('contact[context]', mode);

    let aside = root.querySelector('.nadm-quote-form__context');
    if (!aside) {
      aside = document.createElement('aside');
      aside.className = 'nadm-quote-form__context';
      aside.setAttribute('aria-label', 'Quote context');
      layout.appendChild(aside);
    }

    const safeTitle = escapeHtml(title);
    const safeMeta = escapeHtml(meta || '');
    const safeUrl = escapeHtml(url || '');
    const safeImage = escapeHtml(imageUrl || '');
    const safeNote = escapeHtml(note || '');
    const imageMarkup = safeImage
      ? `<div class="nadm-quote-form__context-media"><img class="nadm-quote-form__context-image" src="${safeImage}" alt="${safeTitle}" loading="lazy" width="800" height="800"></div>`
      : '';
    const metaMarkup = safeMeta ? `<p class="nadm-quote-form__context-meta">${safeMeta}</p>` : '';
    const linkMarkup = safeUrl
      ? `<a class="nadm-quote-form__context-link" href="${safeUrl}">View details</a>`
      : '';
    const badge = mode === 'product' ? 'Product' : 'Collection';

    aside.innerHTML = `
      <div class="nadm-panel nadm-panel--elevated nadm-quote-form__context-panel">
        <span class="nadm-badge nadm-badge--muted nadm-badge--sm">${badge}</span>
        ${imageMarkup}
        <h3 class="nadm-quote-form__context-title">${safeTitle}</h3>
        ${metaMarkup}
        ${linkMarkup}
        <p class="nadm-quote-form__context-note">${safeNote}</p>
      </div>
    `;
  };

  const fillMessage = (text) => {
    if (!message || message.value.trim() === '' || message.dataset.nadmDefault === 'true') {
      if (message) {
        message.value = text;
        message.dataset.nadmDefault = 'true';
      }
    } else if (message && !message.value.includes(text.split(':')[0])) {
      // Keep merchant/user edits; only replace the stock manual prompt.
      const stock = 'I would like a wholesale quote. Please confirm pricing, availability, and lead time.';
      if (message.value.trim() === stock) message.value = text;
    }
  };

  const loadProduct = async (handle) => {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}.js`);
    if (!response.ok) return;
    const product = await response.json();
    const origin = window.location.origin;
    const productUrl = `${origin}/products/${product.handle}`;
    const sku = product.variants?.[0]?.sku || product.vendor || '';
    const imageUrl = product.featured_image || product.images?.[0] || '';

    ensureHidden('contact[product_title]', product.title);
    ensureHidden('contact[product_handle]', product.handle);
    ensureHidden('contact[product_url]', productUrl);
    if (sku) ensureHidden('contact[product_sku]', sku);

    let body = `I would like a quote for: ${product.title}`;
    if (sku) body += ` (SKU: ${sku})`;
    body += `. Product: ${productUrl}. Please confirm pricing, availability, and lead time.`;
    fillMessage(body);

    renderContext({
      mode: 'product',
      title: product.title,
      url: `/products/${product.handle}`,
      meta: sku,
      imageUrl,
      note: 'Your request will include this product so our team can respond with accurate pricing.',
    });
  };

  const loadCollection = async (handle) => {
    const response = await fetch(
      `${window.Shopify?.routes?.root || '/'}collections/${encodeURIComponent(handle)}.js`
    );
    if (!response.ok) return;
    const collection = await response.json();
    const origin = window.location.origin;
    const collectionUrl = `${origin}/collections/${collection.handle}`;
    const count = Array.isArray(collection.products) ? collection.products.length : '';
    const imageUrl = collection.image?.src || collection.products?.[0]?.featured_image || '';

    ensureHidden('contact[collection_title]', collection.title);
    ensureHidden('contact[collection_handle]', collection.handle);
    ensureHidden('contact[collection_url]', collectionUrl);

    const body = `I would like a quote related to the ${collection.title} collection. Collection: ${collectionUrl}. Please confirm pricing, availability, and lead time for the sizes we need.`;
    fillMessage(body);

    renderContext({
      mode: 'collection',
      title: collection.title,
      url: `/collections/${collection.handle}`,
      meta: count !== '' ? `${count} products` : '',
      imageUrl,
      note: 'Your request will include this collection so our team can respond with accurate pricing.',
    });
  };

  if (productHandle) {
    loadProduct(productHandle).catch(() => {});
  } else if (collectionHandle) {
    loadCollection(collectionHandle).catch(() => {});
  }
})();
